import { Load } from "@fatbees/schemas";
import { PendingMutation, TransactionWithMutations } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import {
  deleteAssignment,
  deleteEvent,
  deleteHive,
  deleteLoad,
  putAssignment,
  putDevice,
  putEvent,
  putHive,
  putLoad,
} from "./api";

const HIVES_QUERY_KEY = ["hives"];
const LOADS_QUERY_KEY = ["loads"];
const OBSERVATIONS_QUERY_KEY = ["observations"];
const READINGS_QUERY_KEY = ["readings"];

// TanStack Query's removeQueries destroys in-flight fetches, causing their
// retryer to reject with CancelledError as an unhandled promise rejection.
// This is expected behaviour when we clear stale readings after an assignment
// change — swallow these rejections globally rather than trying to catch them
// per-call (the rejection fires asynchronously from inside query-core's batch).
if (typeof globalThis.addEventListener === "function") {
  globalThis.addEventListener("unhandledrejection", (e) => {
    if (e.reason?.constructor?.name === "CancelledError") {
      e.preventDefault();
    }
  });
}

const DISPLAY_NAMES: Record<string, string> = {
  "hive-assignments": "hive assignment",
  hives: "hive",
  loads: "load",
  events: "event",
  devices: "device",
};

export class EntitySaveError extends Error {
  constructor(
    public readonly collectionId: string,
    public readonly displayName: string,
    public readonly cause: unknown,
  ) {
    const message = cause instanceof Error ? cause.message : "Unknown error";
    super(message);
    this.name = "EntitySaveError";
  }
}

export async function transactionMutationFn(
  { transaction }: { transaction: TransactionWithMutations },
  queryClient: QueryClient,
): Promise<void> {
  const touchedCollections = new Set<string>();

  for (const mutation of transaction.mutations) {
    const { collection, type, modified } = mutation;
    touchedCollections.add(collection.id);

    try {
      await routeMutation(mutation);

      if (type === "insert") {
        collection.utils.writeInsert([modified]);
      } else if (type === "update") {
        collection.utils.writeUpdate(modified);
      } else if (type === "delete") {
        collection.utils.writeDelete(
          (mutation as PendingMutation<Record<string, unknown>, "delete">).key,
        );
      }
    } catch (error) {
      refetchTouchedCollections(touchedCollections, queryClient);
      const displayName = resolveDisplayName(
        collection.id,
        modified,
        queryClient,
      );
      throw new EntitySaveError(collection.id, displayName, error);
    }
  }

  if (touchedCollections.has("hive-assignments")) {
    queryClient.invalidateQueries({ queryKey: HIVES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: OBSERVATIONS_QUERY_KEY });
    queryClient.removeQueries({ queryKey: READINGS_QUERY_KEY });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function routeMutation(mutation: PendingMutation<any>): Promise<void> {
  const { collection, type, modified } = mutation;

  switch (collection.id) {
    case "hive-assignments":
      if (type === "delete") {
        await deleteAssignment(modified);
      } else {
        await putAssignment(modified);
      }
      break;
    case "hives":
      if (type === "delete") {
        await deleteHive(modified);
      } else {
        await putHive(modified);
      }
      break;
    case "loads":
      if (type === "delete") {
        await deleteLoad(modified);
      } else {
        await putLoad(modified);
      }
      break;
    case "events":
      if (type === "delete") {
        await deleteEvent(modified);
      } else {
        await putEvent(modified);
      }
      break;
    case "devices":
      await putDevice(modified);
      break;
    default:
      throw new Error(`Unknown collection: ${collection.id}`);
  }
}

function resolveDisplayName(
  collectionId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modified: any,
  queryClient: QueryClient,
): string {
  if (collectionId === "hives" && modified?.loadId) {
    const loads = queryClient.getQueryData<Load[]>(LOADS_QUERY_KEY);
    const load = loads?.find((l) => l.id === modified.loadId);
    if (load?.isScout) {
      return "scout";
    }
  }
  return DISPLAY_NAMES[collectionId] ?? collectionId;
}

function refetchTouchedCollections(
  touchedCollections: Set<string>,
  queryClient: QueryClient,
) {
  for (const id of touchedCollections) {
    queryClient.refetchQueries({ queryKey: [id] });
  }
}
